import React, { useEffect, useState, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { INSPECTION_CREATE, INSPECTION_UPDATE, INSPECTION_GET } from '../../graphql/inspections';
import { WORK_ORDER_CREATE, WORK_ORDERS_BULK_CREATE } from '../../graphql/workOrders';
import { EMPLOYEE_LIST } from '../../graphql/employees';
import { VEHICLES_LIST } from '../../graphql/vehicles';
import { 
  TextField, 
  Button, 
  MenuItem, 
  Box, 
  CircularProgress, 
  Paper, 
  Typography, 
  Alert, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Chip, 
  Avatar, 
  Grid,
  Divider
} from '@mui/material';
import EditableFormField from '../../components/editable-form-field';
import DatePickerAuto from '../../components/custom-datepicker/DatePickerAuto';
import { useAuthContext } from '../../sections/auth/useAuthContext';
import {
  Build,
  Warning,
  CheckCircle,
  Schedule,
  Title,
  Category,
  Description,
  DirectionsCar,
  Person,
  CalendarToday,
  Notes,
  Assignment,
  Close
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { 
  hasIssues, 
  getIssues, 
  getNewIssues, 
  generateWorkOrderData, 
  createWorkOrdersWithFallback,
  checklistItems,
  checklistLabels,
  workOrderCategories,
  mapInspectionToCategory
} from '../../utils/workOrderUtils';

const statuses = ['Scheduled', 'In Progress', 'Completed', 'Failed'];

const InspectionForm = ({ vehicleId, onClose, isEditing: externalIsEditing, onToggleEdit, isModal = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const [internalIsEditing, setInternalIsEditing] = useState(!isEdit);
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  const setIsEditing = onToggleEdit || setInternalIsEditing;

  // State for work order creation dialog
  const [workOrderDialog, setWorkOrderDialog] = useState({
    open: false,
    inspectionId: null,
    formValues: null
  });

  // Ref to store submitted values for comparison
  const submittedValuesRef = useRef(null);

  const { data: employeesData, loading: employeesLoading } = useQuery(EMPLOYEE_LIST);
  const { data: vehiclesData, loading: vehiclesLoading } = useQuery(VEHICLES_LIST);
  const { data: inspectionData, loading: inspectionLoading } = useQuery(INSPECTION_GET, {
    variables: { id },
    skip: !isEdit,
  });

  const { user } = useAuthContext();

  const [createInspection, { loading: creating }] = useMutation(INSPECTION_CREATE, {
    refetchQueries: ['VehicleGet'],
    onCompleted: async (data) => {
      // Check if the created inspection has issues and auto-create work orders
      const formValues = watch();
      if (checkHasIssues(formValues.checklist)) {
        // Auto-create work orders for each failed item
        await handleAutoCreateWorkOrders(data.inspectionCreate.id, formValues);
      }
      
      // Proceed with normal flow
      if (isModal && onClose) {
        onClose(true);
      } else if (!isModal && onClose) {
        onClose(true);
      } else if (!isModal) {
        navigate('/fleet/inspections');
      }
    },
  });

  const [updateInspection, { loading: updating }] = useMutation(INSPECTION_UPDATE, {
    refetchQueries: ['VehicleGet'],
    onCompleted: async (data) => {
      console.log('Update mutation completed');
      
      // Get the submitted values from ref
      const submittedValues = submittedValuesRef.current;
      console.log('Submitted values:', submittedValues);
      
      if (!submittedValues) {
        console.log('No submitted values found, proceeding with normal flow');
        if (onClose) onClose(true);
        else navigate('/fleet/inspections');
        return;
      }
      
      // Check if there are any issues in the submitted values
      const currentIssues = getIssuesFromChecklist(submittedValues.checklist, submittedValues.checklistNotes);
      console.log('Current issues from submitted values:', currentIssues);
      
      // Get the original inspection data to compare
      const originalInspection = inspectionData?.inspection;
      const originalIssues = getIssuesFromChecklist(originalInspection?.checklist || {}, originalInspection?.checklistNotes || {});
      console.log('Original issues:', originalIssues);
      
      // Check for new or changed issues using utility function
      const newOrChangedIssues = getNewIssues(
        submittedValues.checklist, 
        submittedValues.checklistNotes,
        originalInspection?.checklist || {}, 
        originalInspection?.checklistNotes || {}
      );
      
      console.log('New or changed issues detected:', newOrChangedIssues);
      
      if (newOrChangedIssues.length > 0) {
        console.log('Auto-creating work orders for new/changed issues');
        // Auto-create work orders for new or changed issues
        await handleAutoCreateWorkOrders(id, submittedValues);
      }
      
      // Proceed with normal flow
      if (isModal && onClose) {
        onClose(true);
      } else if (!isModal && onClose) {
        onClose(true);
      } else if (!isModal) {
        navigate('/fleet/inspections');
      }
      
      // Clear the ref
      submittedValuesRef.current = null;
    },
  });

  const [createWorkOrder, { loading: creatingWorkOrder }] = useMutation(WORK_ORDER_CREATE, {
    onCompleted: (data) => {
      // Show success message or navigate
      console.log('Work order created successfully:', data);
    },
    onError: (error) => {
      console.error('Error creating work order:', error);
      console.error('GraphQL errors:', error.graphQLErrors);
      console.error('Network error:', error.networkError);
    },
  });

  const [createBulkWorkOrders, { loading: creatingBulkWorkOrders }] = useMutation(WORK_ORDERS_BULK_CREATE, {
    onCompleted: (data) => {
      console.log('Bulk work orders created successfully:', data);
    },
    onError: (error) => {
      console.error('Error creating bulk work orders:', error);
      console.error('GraphQL errors:', error.graphQLErrors);
      console.error('Network error:', error.networkError);
    },
  });

  // Helper function to check if inspection has issues (using utility function)
  const checkHasIssues = (checklist) => {
    console.log('hasIssues called with checklist:', checklist);
    const hasIssuesResult = hasIssues(checklist);
    console.log('hasIssues result:', hasIssuesResult);
    return hasIssuesResult;
  };

  // Helper function to get issues from checklist (using utility function)
  const getIssuesFromChecklist = (checklist, checklistNotes) => {
    console.log('getIssues called with:', { checklist, checklistNotes });
    const issues = getIssues(checklist, checklistNotes);
    console.log('Total issues found:', issues);
    return issues;
  };

  // Function to create work order from inspection issues
  const handleCreateWorkOrder = async () => {
    const formValues = watch();
    const issues = getIssuesFromChecklist(formValues.checklist, formValues.checklistNotes);
    
    if (issues.length === 0) {
      alert('No issues found to create work order');
      return;
    }

    // Open work order creation dialog
    setWorkOrderDialog({
      open: true,
      inspectionId: id,
      formValues: formValues
    });
  };

  // Function to handle work order creation from dialog
  const handleCreateWorkOrderFromDialog = async () => {
    const { inspectionId, formValues } = workOrderDialog;
    const issues = getIssuesFromChecklist(formValues.checklist, formValues.checklistNotes);
    
    try {
      // Generate work order data using utility function
      const workOrdersData = generateWorkOrderData(issues, formValues, inspectionId, 'manual');

      // Create work orders with fallback
      const result = await createWorkOrdersWithFallback(createBulkWorkOrders, createWorkOrder, workOrdersData);
      
      console.log(`Successfully created ${result.count} work orders using ${result.method} method:`, result);
      
      setWorkOrderDialog({ open: false, inspectionId: null, formValues: null });
      alert(`Successfully created ${result.count} work order(s) for the failed inspection items.`);
      
      // Close inspection form or navigate
      if (onClose) onClose(true);
      else navigate('/fleet/inspections');
    } catch (error) {
      console.error('Error creating work orders:', error);
      alert('Error creating work orders. Please try again.');
    }
  };

  // Function to create work orders automatically after inspection creation/update
  const handleAutoCreateWorkOrders = async (inspectionId, formValues) => {
    console.log('handleAutoCreateWorkOrders called with:', { inspectionId, formValues });
    
    const issues = getIssuesFromChecklist(formValues.checklist, formValues.checklistNotes);
    console.log('Issues found:', issues);
    
    if (issues.length === 0) {
      console.log('No issues found, no work orders needed');
      return;
    }

    try {
      console.log(`Auto-creating ${issues.length} work orders for inspection ${inspectionId}`);
      
      // Generate work order data using utility function
      const workOrdersData = generateWorkOrderData(issues, formValues, inspectionId, 'auto');
      console.log('Bulk work orders data:', workOrdersData);

      // Create work orders with fallback
      const result = await createWorkOrdersWithFallback(createBulkWorkOrders, createWorkOrder, workOrdersData);
      
      console.log(`Auto-created ${result.count} work orders using ${result.method} method:`, result);
      alert(`Automatically created ${result.count} work order(s) for the failed inspection items.`);
      
    } catch (error) {
      console.error('Error auto-creating work orders:', error);
      console.error('Error details:', error.message, error.graphQLErrors, error.networkError);
      alert('Error auto-creating work orders. Please check the work orders manually.');
    }
  };

  // Function to close work order dialog
  const handleCloseWorkOrderDialog = () => {
    setWorkOrderDialog({ open: false, inspectionId: null, formValues: null });
    
    // Close inspection form or navigate
    if (onClose) onClose(true);
    else navigate('/fleet/inspections');
  };

  // Function to get new issues (for updates) using utility function
  const getNewIssuesForDialog = () => {
    if (!workOrderDialog.formValues || !inspectionData?.inspection) return [];
    
    return getNewIssues(
      workOrderDialog.formValues.checklist || {}, 
      workOrderDialog.formValues.checklistNotes || {},
      inspectionData.inspection.checklist || {}, 
      inspectionData.inspection.checklistNotes || {}
    );
  };

  // Function to check if this is an update with new issues
  const isUpdateWithNewIssues = () => {
    return isEdit && getNewIssuesForDialog().length > 0;
  };

  const { control, handleSubmit, reset, formState: { errors }, watch, setValue } = useForm({
    defaultValues: {
      date: new Date().toISOString().slice(0, 10),
      endDate: '',
      vehicleId: vehicleId || '', // Use passed vehicleId if available
      inspector: user?.name || '',
      checklist: checklistItems.reduce((acc, item) => ({ ...acc, [item]: '' }), {}),
      checklistNotes: checklistItems.reduce((acc, item) => ({ ...acc, [item]: '' }), {}),
    },
  });

  useEffect(() => {
    if (isEdit && inspectionData?.inspection) {
      const insp = inspectionData.inspection;
      reset({
        vehicleId: Array.isArray(insp.inspections) ? insp.inspections[0]?.id || '' : insp.inspections?.id || '',
        date: insp.date || '',
        inspector: insp.inspector || '',
        notes: insp.notes || '',
        checklist: insp.checklist || checklistItems.reduce((acc, item) => ({ ...acc, [item]: '' }), {}),
        checklistNotes: insp.checklistNotes || checklistItems.reduce((acc, item) => ({ ...acc, [item]: '' }), {}),
      });
    }
  }, [isEdit, inspectionData, reset]);

  useEffect(() => {
    setValue('inspector', user?.name || '');
  }, [user, setValue]);

  const onSubmit = async (values) => {
    const data = {
      notes: values.notes,
      date: values.date,
      inspector: values.inspector,
      checklist: values.checklist,
      checklistNotes: values.checklistNotes,
      ...(values.vehicleId && { inspections: { connect: { id: values.vehicleId } } }),
    };
    console.log('Submitting inspection with data:', data);
    
    if (isEdit) {
      // Store the submitted values for comparison
      submittedValuesRef.current = { ...values };
      await updateInspection({ variables: { filter: { id }, data } });
    } else {
      await createInspection({ variables: { data } });
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (isEdit) {
      // Reset form to original values
      const insp = inspectionData.inspection;
      reset({
        vehicleId: insp.inspections?.id || '',
        date: insp.date || '',
        inspector: insp.inspector || '',
        notes: insp.notes || '',
        checklist: insp.checklist || checklistItems.reduce((acc, item) => ({ ...acc, [item]: '' }), {}),
      });
      setIsEditing(false);
    } else {
      if (onClose) onClose();
      else navigate('/fleet/inspections');
    }
  };

  if ((isEdit && inspectionLoading) || employeesLoading || vehiclesLoading) return <CircularProgress />;

  const vehicle = isEdit && inspectionData?.inspection?.inspections;
  const formValues = watch();
  const vehicleOptions = vehiclesData?.vehiclesList?.items?.map(veh => ({
    value: veh.id,
    label: `${veh.make} ${veh.model} (${veh.licensePlate})`
  })) || [];

  return (
    <Paper sx={{ p: 4, maxWidth: 1000, mx: 'auto', mt: 4 }}>
      {/* Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ bgcolor: '#10b981', mr: 2 }}>
            <CheckCircle />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {isEdit ? (isEditing ? 'Edit' : 'View') : 'Create'} Inspection
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {isEdit ? 'Update inspection details and checklist' : 'Create a new vehicle inspection'}
            </Typography>
          </Box>
        </Box>
        
        {/* Action Buttons for View Mode - Top */}
        {isEdit && !isEditing && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button 
              onClick={() => navigate('/fleet/inspections')}
              variant="outlined"
              startIcon={<Close />}
            >
              Close
            </Button>
            <Button 
              onClick={handleEdit}
              variant="contained" 
              color="primary"
              sx={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                }
              }}
            >
              Edit
            </Button>
          </Box>
        )}
        
        {/* Close Button for Create Mode - Top */}
        {!isEdit && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              onClick={() => navigate('/fleet/inspections')}
              variant="outlined"
              startIcon={<Close />}
            >
              Close
            </Button>
          </Box>
        )}
        
      </Box>

      {/* Work Order Status Summary */}
      {isEdit && inspectionData?.inspection?.workOrders?.items?.length > 0 && (
        <Box sx={{ 
          mb: 3, 
          p: 2, 
          bgcolor: '#f0f9ff', 
          borderRadius: 2, 
          border: '1px solid #0ea5e9',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Build sx={{ color: '#0ea5e9', fontSize: 24 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0ea5e9' }}>
              Work Orders Generated
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {inspectionData.inspection.workOrders.items.length} work order(s) have been automatically created from failed inspection items.
            </Typography>
          </Box>
        </Box>
      )}

      {isEditing || !isEdit ? (
        <form onSubmit={(e) => {
          e.stopPropagation(); // Prevent event from bubbling up to parent forms
          handleSubmit(onSubmit)(e);
        }}>
          <Grid container spacing={4}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Assignment sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Basic Information
                </Typography>
              </Box>
            </Grid>



            <Grid item xs={12} md={6}>
              <Controller
                name="vehicleId"
                control={control}
                rules={{ required: 'Vehicle is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField 
                    {...field} 
                    select 
                    label="Vehicle" 
                    fullWidth 
                    margin="normal" 
                    disabled={!isEditing}
                    error={!!error}
                    helperText={error?.message}
                    InputProps={{
                      startAdornment: (
                        <DirectionsCar sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  >
                    {vehiclesData?.vehiclesList?.items.map((veh) => (
                      <MenuItem key={veh.id} value={veh.id}>
                        {veh.make} {veh.model} ({veh.licensePlate})
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="inspector"
                control={control}
                render={({ field }) => (
                  <TextField 
                    {...field} 
                    label="Inspector" 
                    fullWidth 
                    margin="normal" 
                    disabled 
                    value={field.value || ''}
                    InputProps={{
                      startAdornment: (
                        <Person sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="date"
                control={control}
                rules={{ required: 'Date is required' }}
                render={({ field, fieldState: { error } }) => (
                  <DatePickerAuto
                    editTab={isEditing}
                    rest={{}}
                    value={field.value}
                    label="Inspection Date"
                    sx={{
                      width: '100%', 
                      mt: "16px", 
                      mb: "8px",
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                    onUpdate={(date) => {
                      field.onChange(date);
                    }}
                    required
                  />
                )}
              />
            </Grid>

            {/* Notes field - Full width for better readability */}
            <Grid item xs={12}>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField 
                    {...field} 
                    label="General Notes" 
                    fullWidth 
                    margin="normal" 
                    multiline 
                    rows={4}
                    disabled={!isEditing}
                    placeholder="Enter any general notes about the inspection..."
                    InputProps={{
                      startAdornment: (
                        <Notes sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                    sx={{ 
                      mt: 2,
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                        '&:focus-within fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                        },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {/* Inspection Checklist */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 4 }}>
                <CheckCircle sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Inspection Checklist
                </Typography>
              </Box>
            </Grid>

            {checklistItems.map((item) => (
              <Grid item xs={12} key={item}>
                <Controller
                  name={`checklist.${item}`}
                  control={control}
                  render={({ field }) => (
                    <Box sx={{ 
                      p: 3, 
                      border: '1px solid #e2e8f0', 
                      borderRadius: 2, 
                      bgcolor: '#f8fafc',
                      mb: 3
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                          {checklistLabels[item]}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant={field.value === 'yes' ? 'contained' : 'outlined'}
                            color="success"
                            size="small"
                            onClick={() => field.onChange('yes')}
                            disabled={!isEditing}
                            sx={{ minWidth: 80 }}
                          >
                            Pass
                          </Button>
                          <Button
                            variant={field.value === 'no' ? 'contained' : 'outlined'}
                            color="error"
                            size="small"
                            onClick={() => field.onChange('no')}
                            disabled={!isEditing}
                            sx={{ minWidth: 80 }}
                          >
                            Fail
                          </Button>
                          <Button
                            variant={field.value === 'issue' ? 'contained' : 'outlined'}
                            color="warning"
                            size="small"
                            onClick={() => field.onChange('issue')}
                            disabled={!isEditing}
                            sx={{ minWidth: 80 }}
                          >
                            Issue
                          </Button>
                        </Box>
                      </Box>
                      
                      {/* Show notes field if Issue is selected */}
                      {field.value === 'issue' && (
                        <Controller
                          name={`checklistNotes.${item}`}
                          control={control}
                          render={({ field: noteField }) => (
                            <TextField
                              {...noteField}
                              label="Issue Details"
                              size="small"
                              fullWidth
                              placeholder={`Describe the issue with ${checklistLabels[item]}`}
                              disabled={!isEditing}
                              multiline
                              minRows={2}
                              sx={{ mt: 1 }}
                            />
                          )}
                        />
                      )}

                      {/* Show notes field if Fail is selected */}
                      {field.value === 'no' && (
                        <Controller
                          name={`checklistNotes.${item}`}
                          control={control}
                          render={({ field: noteField }) => (
                            <TextField
                              {...noteField}
                              label="Failure Details (Optional)"
                              size="small"
                              fullWidth
                              placeholder={`Describe why ${checklistLabels[item]} failed inspection`}
                              disabled={!isEditing}
                              multiline
                              minRows={2}
                              sx={{ mt: 1 }}
                            />
                          )}
                        />
                      )}

                      {/* Show work order creation indicator for failed items */}
                      {(field.value === 'no' || field.value === 'issue') && (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1, 
                          mt: 1.5, 
                          p: 1.5, 
                          bgcolor: '#fef3c7', 
                          borderRadius: 1,
                          border: '1px solid #f59e0b'
                        }}>
                          <Build sx={{ fontSize: 16, color: '#f59e0b' }} />
                          <Typography variant="caption" color="#f59e0b" sx={{ fontWeight: 500 }}>
                            {field.value === 'no' ? 'Will create work order for failed item' : 'Will create work order for issue'}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Show work order status if failed item or issue has linked work order */}
                      {(field.value === 'no' || field.value === 'issue') && inspectionData?.inspection?.workOrders?.items?.some(wo => 
                        wo.workOrderDetails?.includes(checklistLabels[item])
                      ) && (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1, 
                          mt: 1.5, 
                          p: 1.5, 
                          bgcolor: '#f0f9ff', 
                          borderRadius: 1,
                          border: '1px solid #0ea5e9'
                        }}>
                          <Build sx={{ fontSize: 16, color: '#0ea5e9' }} />
                          <Typography variant="caption" color="#0ea5e9" sx={{ fontWeight: 500 }}>
                            Work Order Already Created
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                />
              </Grid>
            ))}

            {/* Failed Items Summary */}
            {isEditing && checkHasIssues(watch().checklist) && (
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 3, 
                  bgcolor: '#fef2f2', 
                  borderRadius: 2, 
                  border: '1px solid #fdcaca',
                  mb: 3
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Warning sx={{ mr: 1, color: '#dc2626' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#dc2626' }}>
                      Failed Items Detected
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    The following items failed inspection and will automatically generate work orders:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {Object.keys(watch().checklist).map((item) => {
                      if (watch().checklist[item] === 'issue') {
                        return (
                          <Chip
                            key={item}
                            label={`${checklistLabels[item]}: ${watch().checklistNotes?.[item] || 'Issue found'}`}
                            color="error"
                            variant="outlined"
                            size="small"
                          />
                        );
                      }
                      return null;
                    })}
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Action Buttons - Bottom */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 3 }}>
                <Button 
                  onClick={handleCancel} 
                  variant="outlined"
                  disabled={creating || updating || creatingBulkWorkOrders}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary" 
                  disabled={creating || updating || creatingBulkWorkOrders}
                  sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                    }
                  }}
                >
                  {creating || updating || creatingBulkWorkOrders ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    isEdit ? 'Update Inspection' : 'Create Inspection'
                  )}
                </Button>
              </Box>
            </Grid>
          </Grid>
          
          {/* Create Work Order Button - Show when not editing and has issues */}
          {!isEditing && isEdit && checkHasIssues(formValues.checklist) && (
            <Box sx={{ mt: 4, p: 3, bgcolor: 'warning.light', borderRadius: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Failed items detected in this inspection. You can create work orders to address these issues.
              </Alert>
              <Button 
                variant="contained" 
                color="warning" 
                onClick={handleCreateWorkOrder}
                disabled={creatingWorkOrder || creatingBulkWorkOrders}
                fullWidth
                sx={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
                  }
                }}
              >
                {creatingWorkOrder || creatingBulkWorkOrders ? 'Creating Work Orders...' : 'Create Work Orders from Failed Items'}
              </Button>
            </Box>
          )}

          {/* Debug: Manual Work Order Creation Test */}
          {isEdit && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f9ff', borderRadius: 2, border: '1px solid #0ea5e9' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Debug: Test work order creation
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const testData = {
                    title: 'Test Work Order - Debug',
                    description: 'Test work order from inspection for debugging',
                    category: 'mechanical',
                    status: 'Open',
                    dateOpened: dayjs().format('YYYY-MM-DD'),
                    workOrderDetails: 'This is a test work order created for debugging purposes',
                    inspections: formValues.vehicleId ? { connect: { id: formValues.vehicleId } } : undefined,
                    inspection: { connect: { id: id || 'test-id' } }
                  };
                  console.log('Creating test work order with data:', testData);
                  createWorkOrder({ variables: { data: testData } });
                }}
              >
                Test Create
              </Button>
            </Box>
          )}
        </form>
      ) : (
        // View Mode
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Assignment sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Inspection Details
              </Typography>
            </Box>
          </Grid>



          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Vehicle</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
              {vehicleOptions.find(v => v.value === formValues.vehicleId)?.label || 'Unassigned'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Inspector</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
              {formValues.inspector || 'Not specified'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Inspection Date</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
              {formValues.date ? dayjs(formValues.date).format('MMM DD, YYYY') : 'Not specified'}
            </Typography>
          </Grid>

          {/* Notes field - Full width for better readability */}
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>General Notes</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
              {formValues.notes || 'No notes provided'}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <CheckCircle sx={{ mr: 1, color: 'success.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Inspection Checklist Results
              </Typography>
            </Box>
          </Grid>

          {Object.keys(formValues.checklist).map((item) => (
            <Grid item xs={12} md={6} key={item}>
              <Box sx={{ 
                p: 3, 
                border: '1px solid #e2e8f0', 
                borderRadius: 2, 
                bgcolor: '#f8fafc',
                mb: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                    {checklistLabels[item]}
                  </Typography>
                  <Chip
                    label={formValues.checklist[item] || 'Not checked'}
                    color={
                      formValues.checklist[item] === 'yes' ? 'success' : 
                      formValues.checklist[item] === 'no' ? 'error' : 
                      formValues.checklist[item] === 'issue' ? 'warning' : 'default'
                    }
                    size="small"
                  />
                </Box>
                
                {/* Show issue note if present */}
                {formValues.checklist[item] === 'issue' && formValues.checklistNotes && formValues.checklistNotes[item] && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Issue Details: {formValues.checklistNotes[item]}
                  </Typography>
                )}
                
                {/* Show work order status if issue has linked work order */}
                {formValues.checklist[item] === 'issue' && inspectionData?.inspection?.workOrders?.items?.some(wo => 
                  wo.workOrderDetails?.includes(checklistLabels[item])
                ) && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    mt: 1.5, 
                    p: 1, 
                    bgcolor: '#f0f9ff', 
                    borderRadius: 1,
                    border: '1px solid #0ea5e9'
                  }}>
                    <Build sx={{ fontSize: 16, color: '#0ea5e9' }} />
                    <Typography variant="caption" color="#0ea5e9" sx={{ fontWeight: 500 }}>
                      Work Order Created
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          ))}
          
          {/* Show linked work orders with enhanced traceability */}
          {inspectionData?.inspection?.workOrders?.items?.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Build sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Linked Work Orders ({inspectionData.inspection.workOrders.items.length})
                </Typography>
              </Box>
              
              {/* Enhanced work order display with traceability */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {inspectionData.inspection.workOrders.items.map((workOrder) => (
                  <Box key={workOrder.id} sx={{ 
                    p: 3, 
                    border: '1px solid #e2e8f0', 
                    borderRadius: 2, 
                    bgcolor: '#fef2f2',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: '#fee2e2',
                      borderColor: '#dc2626'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }} onClick={() => navigate(`/fleet/work-orders/${workOrder.id}`)}>
                    
                    {/* Work Order Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#dc2626', mb: 0.5 }}>
                          {workOrder.title || `Work Order #${workOrder.id}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Created from Inspection Issue
                        </Typography>
                      </Box>
                      <Chip 
                        label={workOrder.status || 'Open'} 
                        size="small"
                        color={
                          workOrder.status === 'Completed' ? 'success' : 
                          workOrder.status === 'In Progress' ? 'warning' : 
                          workOrder.status === 'Cancelled' ? 'error' : 'default'
                        }
                      />
                    </Box>
                    
                    {/* Work Order Details */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                      {workOrder.category && (
                        <Chip 
                          label={workOrderCategories.find(cat => cat.value === workOrder.category)?.label || workOrder.category} 
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                      <Chip 
                        label={`Status: ${workOrder.status || 'Open'}`}
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                      {workOrder.dateOpened && (
                        <Chip 
                          label={`Opened: ${dayjs(workOrder.dateOpened).format('MMM DD, YYYY')}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    
                    {/* Work Order Description */}
                    {workOrder.workOrderDetails && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          whiteSpace: 'pre-line',
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          bgcolor: '#f8fafc',
                          p: 1.5,
                          borderRadius: 1,
                          border: '1px solid #e2e8f0'
                        }}>
                          {workOrder.workOrderDetails}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Traceability Information */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1, 
                      p: 1.5, 
                      bgcolor: '#f0f9ff', 
                      borderRadius: 1,
                      border: '1px solid #0ea5e9'
                    }}>
                      <Assignment sx={{ fontSize: 16, color: '#0ea5e9' }} />
                      <Typography variant="caption" color="#0ea5e9" sx={{ fontWeight: 500 }}>
                        This work order was automatically generated from a failed inspection item
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      )}
      
      {/* Work Order Creation Dialog */}
      <Dialog 
        open={workOrderDialog.open} 
        onClose={handleCloseWorkOrderDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          {isUpdateWithNewIssues() ? 'Create Work Orders from New/Changed Failed Items' : 'Create Work Orders from Failed Inspection Items'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {isUpdateWithNewIssues() ? 'New or changed failed items found in inspection:' : 'Failed items found in inspection:'}
            </Typography>
            {(isUpdateWithNewIssues() ? getNewIssuesForDialog() : (workOrderDialog.formValues ? getIssuesFromChecklist(workOrderDialog.formValues.checklist || {}, workOrderDialog.formValues.checklistNotes || {}) : [])).map((issue, index) => (
              <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Warning sx={{ fontSize: 16, mr: 1, color: 'warning.main' }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {issue.component}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  {issue.details}
                </Typography>
                <Chip 
                  label={workOrderCategories.find(cat => cat.value === issue.category)?.label || issue.category} 
                  size="small"
                  sx={{ mt: 0.5, ml: 2 }}
                />
              </Box>
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Work orders will be created with "Open" status and linked to this inspection and vehicle.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseWorkOrderDialog} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleCreateWorkOrderFromDialog} 
            variant="contained" 
            color="primary"
            disabled={creatingWorkOrder || creatingBulkWorkOrders}
            sx={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
              }
            }}
          >
            {creatingWorkOrder || creatingBulkWorkOrders ? 'Creating...' : 'Create Work Orders'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default InspectionForm; 